import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ParticipantDetail } from "@asd/customer-portal-api";
import { useOrganization } from "@asd/customer-portal-ui";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, Collapse, ListItem, ListItemButton, ListItemText, Stack, Typography, useTheme } from "@mui/material";
import { List, RowComponentProps, useListRef } from "react-window";

import { ParticipantSelectionDetailsProps } from "./ParticipantSelection";

const ParticipantSelectionAccordion = memo(
  ({
    row,
    renderRow,
    pendingSelectedPPT,
    scrollToRowAccordionRef,
    trimmedSearchTerm,
  }: {
    row: { caseManagerName: string; participantDetails: ParticipantDetail[] | null };
    renderRow: (_props: RowComponentProps | ParticipantDetail) => React.JSX.Element;
    pendingSelectedPPT: ParticipantDetail | null;
    scrollToRowAccordionRef: React.RefObject<HTMLDivElement | null>;
    trimmedSearchTerm: string;
  }) => {
    const theme = useTheme();
    const {
      configuration: { participantIdentifier },
    } = useOrganization();

    const [open, setOpen] = useState(false);

    const { caseManagerName, participantDetails } = row;

    const { isSelectedPPTInThisCaseManager, isCaseManagerSearched, isAnyParticipantSearched } = useMemo(() => {
      const isSelectedPPTInThisCaseManager = (participantDetails ?? []).some(
        (ppt) => ppt.participantId === pendingSelectedPPT?.participantId,
      );

      const isCaseManagerSearched =
        !!trimmedSearchTerm.length && caseManagerName.toLowerCase().replace(/\s/g, "").includes(trimmedSearchTerm);

      const isAnyParticipantSearched =
        !!trimmedSearchTerm.length &&
        (participantDetails ?? []).some((ppt) => {
          const fullName = `${ppt.firstName}${ppt.lastName}`.toLowerCase().replace(/\s/g, "");
          const participantSelectionId = ppt[participantIdentifier.key]?.toLowerCase().replace(/\s/g, "");
          return fullName.includes(trimmedSearchTerm) || participantSelectionId?.includes(trimmedSearchTerm);
        });

      return { isSelectedPPTInThisCaseManager, isCaseManagerSearched, isAnyParticipantSearched };
    }, [trimmedSearchTerm, caseManagerName, pendingSelectedPPT, participantDetails, participantIdentifier]);

    useEffect(() => {
      setOpen(isSelectedPPTInThisCaseManager || isAnyParticipantSearched);
    }, [isSelectedPPTInThisCaseManager, isAnyParticipantSearched]);

    return (
      <Stack
        direction="column"
        gap={1}
        sx={[
          {
            borderRadius: 2,
          },
          open && {
            border: `1px solid ${theme.palette.divider}`,
            mb: 1,
          },
          open &&
            isCaseManagerSearched && {
              outline: `2px solid ${theme.palette.secondary.main}`,
            },
        ]}
        width="100%"
      >
        <ListItemButton
          ref={isSelectedPPTInThisCaseManager ? scrollToRowAccordionRef : null}
          onClick={() => setOpen(!open)}
          sx={[
            {
              gap: 1,
              borderRadius: 2,
              py: 0,
              backgroundColor: theme.palette.background.paper,
              zIndex: 1,

              "&:hover": {
                backgroundColor: `${theme.palette.grey[200]} !important`,
              },
            },
            open && {
              borderBottom: `1px solid ${theme.palette.divider}`,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              position: "sticky",
              top: theme.spacing(-2),
            },
            !open && {
              border: `1px solid ${theme.palette.divider}`,
            },
            open &&
              isCaseManagerSearched && {
                borderBottomColor: theme.palette.secondary.main,
              },
            !open &&
              isCaseManagerSearched && {
                border: "none",
                outline: `2px solid ${theme.palette.secondary.main}`,
              },
          ]}
        >
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          <Typography sx={{ flexGrow: 1 }} variant="h6">
            {caseManagerName}
          </Typography>
        </ListItemButton>
        <Box px={1}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Stack direction="column" pb={1} spacing={1}>
              {participantDetails?.map(renderRow)}
            </Stack>
          </Collapse>
        </Box>
      </Stack>
    );
  },
);

ParticipantSelectionAccordion.displayName = "ParticipantSelectionAccordion";

const ParticipantSelectionList = ({
  sortedParticipantDetails,
  pendingSelectedPPT,
  setPendingSelectedPPT,
  trimmedSearchTerm,
  userIsCaseManager,
}: ParticipantSelectionDetailsProps) => {
  const theme = useTheme();

  const {
    configuration: { participantIdentifier, supportCoordinator },
  } = useOrganization();

  const [needsScroll, setNeedsScroll] = useState<boolean>(false);

  const listRef = useListRef(null);
  const scrollToRowAccordionRef = useRef<HTMLDivElement>(null);
  const scrollToRowRef = useRef<HTMLDivElement>(null);

  const structuredParticipants = useMemo(
    () =>
      userIsCaseManager
        ? []
        : Array.from(
            sortedParticipantDetails.reduce((result, participant) => {
              const caseManagerName = participant.caseManagerName ?? "Unassigned";

              if (!result.get(caseManagerName)) {
                result.set(caseManagerName, []);
              }

              result.get(caseManagerName)?.push(participant);

              return result;
            }, new Map<string, ParticipantDetail[]>()),
            ([key, value]) => ({
              caseManagerName: key,
              participantDetails: value,
            }),
          ),
    [userIsCaseManager, sortedParticipantDetails],
  );

  const renderRow = useCallback(
    (props: RowComponentProps | ParticipantDetail) => {
      let rowParticipantDetails = props as ParticipantDetail;
      let style = {};

      if ("index" in props) {
        style = props.style;
        rowParticipantDetails = (sortedParticipantDetails[props.index] ?? {}) as ParticipantDetail;
      }

      const { firstName, lastName, participantId } = rowParticipantDetails;
      const participantSelectionId = rowParticipantDetails[participantIdentifier.key];

      const selected = pendingSelectedPPT?.participantId === participantId;

      const isParticipantSearched = !!(
        !!trimmedSearchTerm.length &&
        (`${firstName}${lastName}`.toLowerCase().replace(/\s/g, "").includes(trimmedSearchTerm) ||
          participantSelectionId?.toLowerCase().replace(/\s/g, "").includes(trimmedSearchTerm))
      );

      return (
        <ListItem key={participantId} component="div" style={style} sx={{ whiteSpace: "nowrap" }} disablePadding>
          <ListItemButton
            ref={selected ? scrollToRowRef : null}
            onClick={() => {
              setPendingSelectedPPT(rowParticipantDetails);
            }}
            selected={selected}
            sx={[
              {
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                py: 0,
                scrollMarginTop:
                  parseFloat(theme.spacing(1)) + (scrollToRowAccordionRef.current ? scrollToRowAccordionRef.current.offsetHeight : 0),

                "&.Mui-selected, &.Mui-selected:hover": {
                  backgroundColor: theme.palette.overrides.participantSelectionSelectedBackground.main,
                },

                "&:not(.Mui-selected):hover": {
                  backgroundColor: theme.palette.action.focus,
                },
              },
              isParticipantSearched && {
                border: `2px solid ${theme.palette.secondary.main}`,
              },
            ]}
          >
            <ListItemText
              primary={`${firstName} ${lastName}`}
              secondary={`${participantIdentifier.shortLabel}: ${participantSelectionId}`}
              sx={{
                "& .MuiTypography-root": {
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                },
              }}
            />
          </ListItemButton>
        </ListItem>
      );
    },
    [pendingSelectedPPT?.participantId, trimmedSearchTerm, theme, sortedParticipantDetails, setPendingSelectedPPT, participantIdentifier],
  );

  useEffect(() => {
    if (pendingSelectedPPT) {
      setTimeout(() => {
        setNeedsScroll(true);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (needsScroll) {
      if (listRef.current && pendingSelectedPPT) {
        const index = sortedParticipantDetails.findIndex((ppt) => ppt.participantId === pendingSelectedPPT.participantId);
        if (index >= 0) {
          listRef.current.scrollToRow({
            align: "start",
            behavior: "instant",
            index,
          });
        }
      } else if (scrollToRowRef.current) {
        scrollToRowRef.current.scrollIntoView({ behavior: "instant", block: "start" });
      }

      setNeedsScroll(false);
    }
  }, [listRef, needsScroll, pendingSelectedPPT, sortedParticipantDetails]);

  return userIsCaseManager || !supportCoordinator.enabled ? (
    <List
      listRef={listRef}
      overscanCount={5}
      rowComponent={renderRow}
      rowCount={sortedParticipantDetails.length}
      rowHeight={65}
      rowProps={{} as ParticipantDetail}
      style={{
        height: "100%",
        width: "100%",
      }}
    />
  ) : (
    structuredParticipants.map((row) => (
      <ParticipantSelectionAccordion
        key={row.caseManagerName}
        pendingSelectedPPT={pendingSelectedPPT}
        renderRow={renderRow}
        row={row}
        scrollToRowAccordionRef={scrollToRowAccordionRef}
        trimmedSearchTerm={trimmedSearchTerm}
      />
    ))
  );
};

export default memo(ParticipantSelectionList);
